/**
 * 游戏皮肤相关 Store
 * 负责皮肤数据的加载、管理和缓存
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useCacheStore } from '../cache'
import { cacheKeys } from '../config'
import { skinsApi } from '@/https/api/skins'
import type { GameSkin, GameSkinCreateRequest, GameSkinUpdateRequest } from '@/https/types/skins'

export const useSkinsStore = defineStore('skins', () => {
    const cacheStore = useCacheStore()

    // ==================== 状态管理 ====================

    // 皮肤列表
    const skinList = ref<GameSkin[]>([])

    // 加载状态
    const skinListLoading = ref(false)

    // 操作加载状态
    const skinOperationLoading = ref(false)

    // 错误信息
    const skinListError = ref<string | null>(null)

    // ==================== 计算属性 ====================

    // 是否有皮肤数据
    const hasSkinList = computed(() => skinList.value.length > 0)

    // 皮肤总数
    const skinCount = computed(() => skinList.value.length)

    // 按排序权重排序的皮肤列表
    const sortedSkinList = computed(() => {
        return [...skinList.value].sort((a, b) => {
            // 排序权重小的排在前面，没有权重的排在后面
            const orderA = a.skin_order ?? 999999
            const orderB = b.skin_order ?? 999999
            return orderA - orderB
        })
    })

    // 皮肤选项（用于下拉选择）
    const skinOptions = computed(() => {
        return sortedSkinList.value.map(skin => ({
            label: skin.skin_name,
            value: skin.id,
            image_url: skin.image_url || null,
        }))
    })

    // ==================== 数据加载 ====================

    /**
     * 加载所有皮肤数据
     * @param forceRefresh 是否强制刷新
     */
    const loadSkinList = async (forceRefresh = false): Promise<GameSkin[]> => {
        try {
            skinListLoading.value = true
            skinListError.value = null

            const response = await cacheStore.smartCache(
                cacheKeys.config.skins,
                () => {
                    // 发布页需要完整选项列表，避免默认分页大小变化后再次漏皮肤。
                    return skinsApi.getAllSkins({ page: 1, size: 100 })
                },
                forceRefresh
            )
            // 处理分页响应格式：提取 items 字段
            let skinsArray: GameSkin[] = []
            if (Array.isArray(response)) {
                // 如果直接是数组（未分页）
                skinsArray = response
            } else if (response && typeof response === 'object' && 'items' in response) {
                // 如果是分页格式，提取 items
                skinsArray = Array.isArray(response.items) ? response.items : []
            }

            skinList.value = skinsArray
            return skinList.value
        } catch (error) {
            console.error('❌ [SkinStore] 加载皮肤数据失败:', error)
            skinListError.value = error instanceof Error ? error.message : '加载皮肤数据失败'
            return []
        } finally {
            skinListLoading.value = false
        }
    }

    const setSkinList = (items: GameSkin[]) => {
        skinList.value = Array.isArray(items) ? items : []
        skinListError.value = null
    }

    /**
     * 刷新皮肤数据
     */
    const refreshSkinList = async (): Promise<GameSkin[]> => {
        return await loadSkinList(true)
    }

    // ==================== 数据操作（管理员功能） ====================

    /**
     * 创建皮肤
     * @param skinData 皮肤数据
     */
    const createSkin = async (skinData: GameSkinCreateRequest): Promise<boolean> => {
        try {
            skinOperationLoading.value = true
            skinListError.value = null

            await skinsApi.createSkin(skinData)

            // 重新加载皮肤列表
            await loadSkinList(true)

            return true
        } catch (error) {
            console.error('创建皮肤失败:', error)
            skinListError.value = error instanceof Error ? error.message : '创建皮肤失败'
            return false
        } finally {
            skinOperationLoading.value = false
        }
    }

    /**
     * 更新皮肤
     * @param id 皮肤ID
     * @param skinData 皮肤数据
     */
    const updateSkin = async (id: number, skinData: GameSkinUpdateRequest): Promise<boolean> => {
        try {
            skinOperationLoading.value = true
            skinListError.value = null

            await skinsApi.updateSkin(id, skinData)

            // 重新加载皮肤列表
            await loadSkinList(true)

            return true
        } catch (error) {
            console.error('更新皮肤失败:', error)
            skinListError.value = error instanceof Error ? error.message : '更新皮肤失败'
            return false
        } finally {
            skinOperationLoading.value = false
        }
    }

    /**
     * 删除皮肤
     * @param id 皮肤ID
     */
    const deleteSkin = async (id: number): Promise<boolean> => {
        try {
            skinOperationLoading.value = true
            skinListError.value = null

            await skinsApi.deleteSkin(id)

            // 重新加载皮肤列表
            await loadSkinList(true)

            return true
        } catch (error) {
            console.error('删除皮肤失败:', error)
            skinListError.value = error instanceof Error ? error.message : '删除皮肤失败'
            return false
        } finally {
            skinOperationLoading.value = false
        }
    }

    // ==================== 工具方法 ====================

    /**
     * 根据ID查找皮肤
     * @param id 皮肤ID
     */
    const findSkinById = (id: number): GameSkin | null => {
        return skinList.value.find(skin => skin.id === id) || null
    }

    /**
     * 根据名称查找皮肤
     * @param name 皮肤名称
     */
    const findSkinByName = (name: string): GameSkin | null => {
        return skinList.value.find(skin => skin.skin_name === name) || null
    }

    /**
     * 清除错误信息
     */
    const clearSkinListError = (): void => {
        skinListError.value = null
    }

    // ==================== 初始化 ====================

    /**
     * 初始化皮肤数据
     */
    const initSkins = async (): Promise<void> => {
        try {
            await loadSkinList()
        } catch (error) {
            console.error('初始化皮肤数据失败:', error)
        }
    }

    // ==================== 导出 ====================

    return {
        // 状态 - 直接返回ref，保持响应式
        skinList,
        skinListLoading,
        skinOperationLoading,
        skinListError,

        // 计算属性
        hasSkinList,
        skinCount,
        sortedSkinList,
        skinOptions,

        // 数据加载
        loadSkinList,
        setSkinList,
        refreshSkinList,

        // 数据操作
        createSkin,
        updateSkin,
        deleteSkin,

        // 工具方法
        findSkinById,
        findSkinByName,
        clearSkinListError,

        // 初始化
        initSkins
    }
})
